import { currencies } from '~/constants/currency';
import Address from '~/types/Address';
import APIAddress from '~/types/APIAddress';
import { requiresCustomsDeclaration as isCustomsRequired } from '~/types/CustomsDeclaration';
import CustomsItem from '~/types/CustomsItem';
import LineItem from '~/types/LineItem';
import Order, { OrderSelection } from '~/types/Order';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import { Parcel } from '~/types/Parcel';
import { isRowEntryFulfilled } from '~/utils/batchEntryRendering';
import { TranslationFunction } from '~/utils/translate';

export function batchEntriesBuyMessage(count: number, t: TranslationFunction, showNext?: boolean) {
  if (count === 0) {
    return t('orders:drawers:buyLabel:buyLabel');
  }

  return showNext && count === 1
    ? t('orders:phrases:next')
    : t('orders:drawers:buyLabel:buyLabelWithCount', { count });
}

export function batchEntriesOrdersCountMessage(
  selectedOrdersCount: number,
  batchEntriesToBuyCount: number,
  t: TranslationFunction,
) {
  if (selectedOrdersCount === 0) {
    return '';
  }

  return t('orders:actions:multi:readyToBuy', {
    count: batchEntriesToBuyCount,
    total: selectedOrdersCount,
  });
}

const MAX_ORDER_NUMBER_CHAR = 25;
export function singleOrderMessage(orderNumber: string) {
  if (!orderNumber) return;

  const orderNumberLabel =
    orderNumber.length > MAX_ORDER_NUMBER_CHAR
      ? `...${orderNumber.slice(-MAX_ORDER_NUMBER_CHAR)}`
      : orderNumber;

  return orderNumberLabel;
}

export function getSelectedBatchEntriesById(
  batchEntryIds: number[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
) {
  return batchEntryIds.map((id) => batchEntryData[id]).filter((be) => !!be);
}

export function getSelectedFulfilledOrders(
  batchEntryIds: number[],
  ordersData: Order[],
  batchEntryData: HashMap<number, BatchEntryExtended>,
) {
  const ordersDataHashmap = ordersData.reduce<Record<string, Order | undefined>>((accum, curr) => {
    accum[curr.objectId] = curr;
    return accum;
  }, {});

  const selectedBatchEntries = getSelectedBatchEntriesById(batchEntryIds, batchEntryData);

  return selectedBatchEntries.filter((batchEntry) => {
    const order = ordersDataHashmap[batchEntry.orderObjectId];
    return !!order && isRowEntryFulfilled(order, false, batchEntry);
  });
}

export function getParcelsFromOrdersAndBatchEntries(
  selectedOrders: OrderSelection,
  batchEntries: BatchEntryExtended[],
  ordersData: {
    [orderId: string]: Order;
  },
) {
  const parcels: Parcel[] = [];

  Object.entries(selectedOrders).forEach(([orderId, orderBatchEntryIds]) => {
    if (orderBatchEntryIds.length) {
      return;
    }
    const order = ordersData[orderId];
    if (order.parcel) {
      parcels.push(order.parcel);
    }
  });

  batchEntries.forEach((batchEntry) => {
    if (batchEntry.parcel) {
      parcels.push({
        ...batchEntry.parcel,
        useImportedOrderWeight: batchEntry.useImportedWeight,
      });
    }
  });

  return parcels;
}

export function shouldShowCustomsCard({
  orderRequiresCustoms,
  isBypassCustoms,
}: {
  orderRequiresCustoms: boolean;
  isBypassCustoms: boolean;
}) {
  return orderRequiresCustoms && !isBypassCustoms;
}

export function singleOrMultiSelectionShouldShowCustoms(
  batchEntries: BatchEntryExtended[],
  ordersData: { [orderId: string]: Order },
  selectedOrders: OrderSelection,
  defaultUserAddress?: APIAddress,
): boolean {
  for (const [orderId, batchEntryIds] of Object.entries(selectedOrders)) {
    // If value is not an empty array then this order has batchEntry
    // We ignore the order in this section
    if (batchEntryIds.length) {
      continue;
    }

    // Find the order from the hash
    const order = ordersData[orderId];

    if (order) {
      const fromAddress = order.addressFrom || defaultUserAddress;
      if (order.toAddress && fromAddress && isCustomsRequired(order.toAddress, fromAddress)) {
        return true;
      }
    }
  }

  for (const entry of batchEntries) {
    if (
      entry.addressFrom &&
      entry.addressTo &&
      isCustomsRequired(entry.addressTo, entry.addressFrom)
    ) {
      return true;
    }
  }

  return false;
}

export const mapCustomsItemToLineItem = (item: CustomsItem): LineItem => {
  const {
    id,
    description,
    skuCode,
    quantity,
    originalUnitWeight,
    originalMassUnit,
    originCountry,
    unitValue,
    valueAmount,
    originalNetWeight,
  } = item;

  return {
    objectId: '',
    id,
    product: description,
    variant: '',
    appId: '',
    title: description,
    sku: skuCode || '',
    quantity: quantity,
    isFulfilled: false,
    totalAmount: valueAmount.toString(),
    unitAmount: unitValue.toString(),
    currency: currencies.USD,
    totalWeight: originalNetWeight,
    unitWeight: originalUnitWeight,
    weightUnit: originalMassUnit,
    manufactureCountry: originCountry.name,
    maxShipTime: undefined,
    maxDeliveryTime: undefined,
    hsCode: undefined,
    variantTitle: '',
    countryOfOrigin: originCountry,
  };
};

export const mapCustomsItemsToLineItems = (items?: CustomsItem[] | null): LineItem[] => {
  if (!items) {
    return [];
  }
  return items.map(mapCustomsItemToLineItem);
};

export const getLineItemsFromSelectionOrCustoms = (
  lineItems: LineItem[],
  customItems: CustomsItem[],
  shouldShowCustoms?: boolean,
): LineItem[] => {
  return shouldShowCustoms ? mapCustomsItemsToLineItems(customItems) : lineItems;
};

export function getToAddress(batchEntry?: BatchEntryExtended, order?: Order): Address | null {
  return batchEntry?.addressTo || order?.toAddress || null;
}

export function getParcel(batchEntry?: BatchEntryExtended, order?: Order): Parcel | undefined {
  return (batchEntry || order)?.parcel || undefined;
}
